/**
 * 작성자 : 홍선영
 * 날짜 : 2024.09.23
 * 기능 : 시/분/초 선택 컴포넌트
 *
 * 수정자 : 홍선영
 * 날짜 : 2024.09.26
 * 기능 : 선택가능 시간대 여부에 따른 disabled 기능 주석처리
 *
 * 수정자 : 박승호
 * 날짜 : 2025.01.22
 * 기능 : visibility hidden을 display none으로 변경 ( 빈 여백 보단 꽉차게 보여주기 위함 ), 특정 시간 disalbed 기능 적용
 */

import { t } from 'i18next';
import React, { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { BtnGhost } from './Button';
import { IDaySchedule } from 'customTypes';
import { addOneSecond, formatTime, parseTime, subtractOneSecond } from '../utils/formatDate';
import { INIT_END_HMS, INIT_START_HMS } from '../_constants';

const TimePickerContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const TimeInput = styled.input`
  max-width: 8rem;
  text-align: center;
`;

const PickerButton = styled.button`
  cursor: pointer;
  background: var(--tonal);
  color: var(--text-primary);
  height: 2.5rem;
  // min-width: 6rem;
  font-size: 0.875rem;
  width: 100%;
  gap: 0.25rem;

  &.active {
    color: var(--text-reverse);
    background-color: var(--filled-blue-deep);
  }

  &:hover:not(.active, .disabled) {
    background-color: var(--tonal-deep);
  }

  &.disabled {
    color: var(--text-disabled);
    background-color: var(--tonal);

    &:hover {
      cursor: default;
    }
  }

  span.material-symbols-rounded {
    font-size: 1rem;
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }
`;

const PickerDropdown = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0.125rem;
  position: absolute;
  top: 100%;
  left: 0;
  background: var(--board);
  border: 1px solid var(--outline);
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  padding: 10px;
  display: flex;
  /* gap: 10px; */
  z-index: 9999;
  border-radius: 5px;
  color: var(--text-secondary);
`;

const Btns = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;

  button {
    cursor: pointer;
    user-select: none;
    padding: 0.1rem 0.5rem;
    height: fit-content;
    width: fit-content;
    min-width: fit-content;
  }
`;

const TimeHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: var(--board);
  text-align: center;
  user-select: none;
  padding: 5px 10px;
  font-size: 0.875rem;
`;

const TimeColumn = styled.div`
  max-height: 150px;
  overflow-y: scroll;
  position: relative;
  scrollbar-gutter: stable;

  /* PC 환경일 경우에만 */
  @media screen and (pointer: Fine) and (min-width: 1025px) {
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #ccc;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-track {
      background-color: #f0f0f0;
    }
  }
`;

const TimeOption = styled.div<{ disabled: boolean }>`
  padding: 5px 10px;
  text-align: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  background-color: var(--board);
  border-radius: 5px;
  font-size: 0.875rem;
  user-select: none;

  &:hover {
    background-color: ${({ disabled }) => (disabled ? 'transparent' : 'var(--tonal-blue)')};
  }

  &.active {
    color: var(--primary);
    background-color: var(--tonal-blue);
  }

  &:hover:not(.active) {
    background-color: var(--tonal-deep);
  }
`;

interface Time {
  h: number;
  m: number;
  s: number;
}

interface TimePickerProps {
  type: 'start' | 'end';
  mode: 'input' | 'picker'; // input = 시간 입력 및 picker 기능 모두 제공, picker = picker 기능만 제공
  setSelectedTime: (time: Time) => void;
  startTime?: Time;
  selectedTime: Time;
  availableTimeList: [{ start: Time; end: Time }]; // 선택가능한 시간대 배열
  availableTimeRange: { start: Time; end: Time };
  setAvailableTimeRange: Dispatch<SetStateAction<{ start: Time; end: Time }>>;
  // invalid: boolean;
  schedules?: IDaySchedule[];
  isNewSchedule: boolean;
  className?: string;
  isDisabled?: boolean;
  showSecond?: boolean; // 초 단위 노출 여부
  useAutoRef?: boolean; // useRef 스크롤 포커스이동 여부
  minUnit?: 1 | 5 | 10 | 15 | 20 | 30; // 분 목록 - 1이면 분단위 / 10이면 10분단위 / 30이면 30분단위 여부
  hourRange?: [0, 28] | [9, 17] | [10, 18]; // 시간 목록 범위
  limitTime?: Time; // 특정 제한할 ( 시간 / 분 / 초 )
  limitRange?: 'up' | 'down'; // up - 제한할 시간보다 위에 시간들을 Disabled, down - 제한한 시간보다 아래 시간들을 Disabled )
  isIcon?: boolean; // 아이콘 표시 여부
  setIsUpdated?: (isUpdated: boolean) => void;
}

const CustomTimePicker: React.FC<TimePickerProps> = ({
  type,
  mode,
  setSelectedTime,
  startTime,
  selectedTime,
  availableTimeList,
  availableTimeRange,
  setAvailableTimeRange,
  schedules,
  isNewSchedule,
  className,
  isDisabled,
  showSecond = true,
  useAutoRef = true,
  minUnit = 1,
  hourRange = [0, 28],
  limitTime,
  limitRange,
  isIcon = false,
  setIsUpdated,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [_invalid, _setInvalid] = useState(false); // 선택한 시간이 유효하지 않을 때의 상태
  const [timeInput, setTimeInput] = useState<string>('');
  const hours = Array.from({ length: hourRange[1] - hourRange[0] + 1 }, (_, i) => hourRange[0] + i);

  const minTime = 60;
  const minArr = Array.from({ length: minTime }, (_, m) => m);
  const minUnitArr = minArr.filter((m: number) => m % minUnit === 0); // 분 유닛별에 대한 배열 생성 15면 [ 0, 15, 30, 45 ], 30이면 [0, 30]

  const pickerRef = useRef<HTMLDivElement>(null);

  const hourWrapRef = useRef<HTMLDivElement>(null);
  const minWrapRef = useRef<HTMLDivElement>(null);
  const secondWrapRef = useRef<HTMLDivElement>(null);

  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const secondRef = useRef<HTMLDivElement>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  const [isSelecting, setIsSelecting] = useState(false);

  // 모든 스케줄값 중 선택 가능한 시간 확인하는 함수
  const isTimeDisabledAmongAllSchedules = (h: number, m: number, s: number) => {
    let disabled = true;
    const totalSeconds = h * 3600 + m * 60 + s;
    availableTimeList.forEach((el) => {
      const startSeconds = el.start.h * 3600 + el.start.m * 60 + el.start.s;
      const endSeconds = el.end.h * 3600 + el.end.m * 60 + el.end.s;
      if (totalSeconds >= startSeconds && totalSeconds <= endSeconds) {
        disabled = false;
      }
    });
    return disabled;
  };

  // 선택 가능한 시간인지 확인하는 함수
  const isTimeDisabled = (h: number, m: number, s: number) => {
    const totalSeconds = h * 3600 + m * 60 + s;
    const startSeconds = availableTimeRange.start.h * 3600 + availableTimeRange.start.m * 60 + availableTimeRange.start.s;
    const endSeconds = availableTimeRange.end.h * 3600 + availableTimeRange.end.m * 60 + availableTimeRange.end.s;
    return totalSeconds < startSeconds || totalSeconds > endSeconds;
  };

  // 선택된 값으로 스크롤을 이동시키는 함수
  const scrollToSelected = (ref: React.RefObject<HTMLDivElement>, wrapRef: React.RefObject<HTMLDivElement>, selectMode: 'smooth' | 'auto', i: number) => {
    if (ref.current && useAutoRef) {
      if (wrapRef.current && ref.current.classList.contains('active')) {
        const rect = ref?.current?.getBoundingClientRect();
        wrapRef.current?.scrollTo({ top: rect.height * i, behavior: selectMode });
      }
      // 아래 코드는 부모 영역에 스크롤 영향 받아서 다른 방안으로 제공
      // ref.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (schedules && startTime) {
      const newScheduleIndex = findNewScheduleIndex({ h: startTime.h, m: startTime.m, s: startTime.m }, schedules);

      if (!isNewSchedule && isTimeDisabled(startTime.h, startTime.m, startTime.s)) {
        const afterTimeRange = schedules[newScheduleIndex + 1];
        const newPrevTime = { endTime: setTimeFormat(startTime) };
        setAvailableTimeRange(getAvailableTimeRange(newPrevTime, afterTimeRange));
      } else {
        const afterTimeRange = schedules[newScheduleIndex];
        const newPrevTime = { endTime: setTimeFormat(startTime) };
        setAvailableTimeRange(getAvailableTimeRange(newPrevTime, afterTimeRange));
      }
    }
  }, [startTime]);

  // 시간 선택 시 호출되는 함수
  const handleTimeSelect = (pickerType: 'hour' | 'min' | 'sec', h: number, m: number, s: number, e: any, mins?: number[]) => {
    e.preventDefault();
    e.stopPropagation();

    const currentTime = { h, m, s };
    setSelectedTime(currentTime);

    // timeInput 업데이트
    const formattedTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}${showSecond ? `:${String(s).padStart(2, '0')}` : ''}`;
    setTimeInput(formattedTime);

    // 특정 제한 시간이 { 0 : 0 : 0 } 일 경우 1시 선택후 00분 선택하고 다시 0시로 선택하면 00분이 선택되는 이슈 방지
    if (limitTime && limitTimeDisabled('min', currentTime, m)) {
      const setMin = (mins && limitRange === 'up' ? mins?.find((time: number) => limitTime.m > time) : mins?.find((time: number) => limitTime.m < time)) || 0; // 선택된 대상이 제한된 시간에 속해 있을 시 제한된 시간 분에서 + 1로 선택되게 설정
      const setHour = limitRange === 'up' ? (setMin < 0 ? h - 1 : h) : setMin > 59 ? h + 1 : h; // 59분이 넘어갈시 시간 + 1
      const updatedTime = { h: setHour, m: setMin, s };
      setSelectedTime(updatedTime);

      const updatedFormattedTime = `${String(setHour).padStart(2, '0')}:${String(setMin).padStart(2, '0')}${showSecond ? `:${String(s).padStart(2, '0')}` : ''}`;
      setTimeInput(updatedFormattedTime);
    }

    if (pickerType === 'hour') {
      // 가장 가까운 가능구간대 찾기
      if (schedules) {
        if (type === 'start' && isNewSchedule) {
          const newScheduleIndex = findNewScheduleIndex(currentTime, schedules);
          const i = isNewSchedule ? 1 : 2;
          const prevTimeRange = schedules[newScheduleIndex - i];
          const afterTimeRange = schedules[newScheduleIndex];
          setAvailableTimeRange(getAvailableTimeRange(prevTimeRange, afterTimeRange));
        }
      }
    }

    if (pickerType === 'hour') {
      scrollToSelected(hourRef, hourWrapRef, 'smooth', h);
    } else if (pickerType === 'min') {
      scrollToSelected(minuteRef, minWrapRef, 'smooth', m);
    } else if (pickerType === 'sec') {
      scrollToSelected(secondRef, secondWrapRef, 'smooth', s);
    }
  };

  const setTimeFormat = (timeObj: Time) => {
    const arr = [];
    for (const val of Object.values(timeObj)) {
      arr.push(val.toString().padStart(2, '0'));
    }

    return `${arr.join(':')}+08:00`;
  };

  const getAvailableTimeRange = (prevRange: any, afterRange: any) => {
    // 이전 스케줄값이 null일때, start from 00:00:00
    const prevEndTime = prevRange ? parseTime(prevRange.endTime) : INIT_START_HMS;

    // 이후 스케줄값이 null일때, end at 23:59:59
    const afterBeginTime = afterRange ? parseTime(afterRange.beginTime) : INIT_END_HMS;

    // 선택 가능한 시간대 계산
    const availableStartTime = addOneSecond(prevEndTime);
    const availableEndTime = subtractOneSecond(afterBeginTime);
    const result = {
      start: formatTime(availableStartTime),
      end: formatTime(availableEndTime),
    };

    return result;
  };

  const findNewScheduleIndex = (time: Time, schedulesParam: IDaySchedule[]) => {
    const newTimeInDecimal = parseInt(String(time.h), 10) + parseInt(String(time.m), 10) / 60 + parseInt(String(time.s), 10) / 3600;
    const index = findInsertionIndex(newTimeInDecimal, schedulesParam);
    return index;
  };

  // 스케줄 배열에서 새로운 시간 데이터의 위치인덱스를 찾는 함수
  const findInsertionIndex = (newTime: number, schedulesParam: IDaySchedule[]) => {
    for (let i = 0; i < schedulesParam.length; i += 1) {
      if (newTime < schedulesParam[i].start) {
        return i;
      }
    }
    return schedulesParam.length;
  };

  const togglePicker = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    setIsOpen(!isOpen);
  };

  // // 시작 시간과 선택된 시간을 비교하여 유효성을 확인하는 함수
  // const compareTimes = (s: Time, e: Time) => {
  //   const startTotalSeconds = s.h * 3600 + s.m * 60 + s.s;
  //   const endTotalSeconds = e.h * 3600 + e.m * 60 + e.s;

  //   return endTotalSeconds >= startTotalSeconds; // 끝 시간이 시작 시간보다 작으면 false 반환
  // };

  // // type === 'end'인 경우, 시작시간보다 이른지 확인하여 invalid 상태값을 업데이트하는 함수
  // const isEarlierThanStartTime = () => {
  //   if (startTime) {
  //     if (compareTimes(startTime, selectedTime)) {
  //       if (_invalid) _setInvalid(false); // 잘못된 상태 초기화
  //     } else {
  //       _setInvalid(true); // 유효하지 않으면 에러 상태 표시
  //     }
  //   }
  // };

  // useEffect(() => {
  //   isEarlierThanStartTime();
  // }, [selectedTime]);

  // 피커 외에 바깥을 클릭했을 때
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false); // 피커를 닫음

        if (mode === 'input') {
          inputRef.current?.blur();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      if (useAutoRef) {
        // 피커가 열렸을 때 선택된 값에 포커스 맞춤
        const minIdx = minUnitArr.findIndex((m) => m === selectedTime.m) || 0;
        scrollToSelected(hourRef, hourWrapRef, 'auto', selectedTime.h);
        scrollToSelected(minuteRef, minWrapRef, 'auto', minIdx);
        scrollToSelected(secondRef, secondWrapRef, 'auto', selectedTime.s);
      }
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const isDisabledFn = (h: number, m: number, s: number) => {
    if (type === 'start') {
      return isTimeDisabledAmongAllSchedules(h, m, s);
    }
    if (type === 'end') {
      return isTimeDisabled(h, m, s);
    }
    return false;
  };

  // 제한 범위 시간 Disabled
  const limitTimeDisabled = (timeType: 'hour' | 'min', currentTime: Time, idx: number) => {
    if (limitTime && type === 'end') {
      if (timeType === 'hour') return limitRange === 'up' ? limitTime.h < idx || (limitTime.h <= idx && limitTime.m === 0) : limitTime.h > idx || (limitTime.h >= idx && limitTime.m === 59);
      if (timeType === 'min') return limitRange === 'up' ? (limitTime.h || 0) - 1 < currentTime.h && limitTime.m <= idx : (limitTime.h || 0) + 1 > currentTime.h && limitTime.m >= idx;
    }
    return false;
  };

  const formatTimeInput = (value: string, isShowSecond: boolean): string => {
    // 숫자와 콜론만 남기고 모두 제거
    const cleaned = value.replace(/[^\d:]/g, '');

    // 콜론으로 분리
    const parts = cleaned.split(':');

    // 숫자만 추출
    const numbers = cleaned.replace(/:/g, '');

    let inputHours = 0;
    let inputMinutes = 0;
    let inputSeconds = 0;

    if (parts.length > 1) {
      // 콜론이 있는 경우
      inputHours = parseInt(parts[0], 10) || 0;
      inputMinutes = parseInt(parts[1], 10) || 0;
      inputSeconds = parseInt(parts[2], 10) || 0;
    } else {
      // 콜론이 없는 경우
      const maxLength = isShowSecond ? 6 : 4;
      const padded = numbers.padStart(Math.min(numbers.length, maxLength), '0');

      if (isShowSecond) {
        if (numbers.length === 3) {
          // 3자리 입력 시 (예: 153)
          inputHours = parseInt(padded.slice(0, 1), 10) || 0;
          inputMinutes = parseInt(padded.slice(1, 3), 10) || 0;
          inputSeconds = 0;
        } else if (numbers.length === 5) {
          // 5자리 입력 시 (예: 43214)
          inputHours = parseInt(padded.slice(0, 1), 10) || 0;
          inputMinutes = parseInt(padded.slice(1, 3), 10) || 0;
          inputSeconds = parseInt(padded.slice(3, 5), 10) || 0;
        } else {
          // 6자리 입력 시 (예: 152432)
          inputHours = parseInt(padded.slice(0, 2), 10) || 0;
          inputMinutes = parseInt(padded.slice(2, 4), 10) || 0;
          inputSeconds = parseInt(padded.slice(4, 6), 10) || 0;
        }
      } else if (numbers.length === 3) {
        // 3자리 입력 시 (예: 432)
        inputHours = parseInt(padded.slice(0, 1), 10) || 0;
        inputMinutes = parseInt(padded.slice(1, 3), 10) || 0;
      } else {
        // 4자리 입력 시 (예: 1543)
        inputHours = parseInt(padded.slice(0, 2), 10) || 0;
        inputMinutes = parseInt(padded.slice(2, 4), 10) || 0;
      }
    }

    // 시간, 분, 초 범위 체크
    if (inputHours > 23) inputHours = 0;
    if (inputMinutes > 59) inputMinutes = 0;
    if (inputSeconds > 59) inputSeconds = 0;

    // 포맷팅
    const formattedHours = String(inputHours).padStart(2, '0');
    const formattedMinutes = String(inputMinutes).padStart(2, '0');

    if (isShowSecond) {
      const formattedSeconds = String(inputSeconds).padStart(2, '0');
      return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }

    return `${formattedHours}:${formattedMinutes}`;
  };

  const onChangeTimeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setTimeInput(value);
  };

  const onBlurTimeInput = (e?: React.FocusEvent<HTMLInputElement>) => {
    // picker 내부 클릭인 경우에는 실행하지 않음
    if (isSelecting) {
      return;
    }

    if (!timeInput.trim()) {
      // 빈 값일 경우 이전 시간값으로 복원
      const prevTime = `${String(selectedTime.h).padStart(2, '0')}:${String(selectedTime.m).padStart(2, '0')}${showSecond ? `:${String(selectedTime.s).padStart(2, '0')}` : ''}`;
      setTimeInput(prevTime);
      return;
    }

    const formattedTime = formatTimeInput(timeInput, showSecond);
    const [h, m, s] = formattedTime.split(':').map(Number);
    scrollToSelected(hourRef, hourWrapRef, 'smooth', h);
    scrollToSelected(minuteRef, minWrapRef, 'smooth', m);
    scrollToSelected(secondRef, secondWrapRef, 'smooth', s || 0);
    setSelectedTime({ h, m, s: s || 0 });
    setTimeInput(formattedTime);
  };

  const onKeyDownTimeInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onBlurTimeInput();
    }
  };

  // selectedTimeStart가 변경될 때 timeInput 업데이트
  useEffect(() => {
    const formattedTime = `${String(selectedTime.h).padStart(2, '0')}:${String(selectedTime.m).padStart(2, '0')}${showSecond ? `:${String(selectedTime.s).padStart(2, '0')}` : ''}`;
    setTimeInput(formattedTime);
  }, [selectedTime, showSecond]);

  const handlePickerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsSelecting(true);
  };

  const handlePickerMouseUp = () => {
    setIsSelecting(false);
  };

  return (
    <TimePickerContainer ref={pickerRef} className={className}>
      {mode === 'input' ? (
        <TimeInput
          type='text'
          ref={inputRef}
          value={timeInput}
          onClick={
            !isDisabled
              ? () => {
                  inputRef.current?.focus();
                  setIsOpen(true);
                }
              : undefined
          }
          onChange={onChangeTimeInput}
          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
          disabled={isDisabled}
          onBlur={onBlurTimeInput}
          onKeyDown={onKeyDownTimeInput}
          placeholder={showSecond ? 'HH:MM:SS' : 'HH:MM'}
          maxLength={showSecond ? 8 : 5} // HH:MM:SS or HH:MM
        />
      ) : (
        <>
          {/* 버튼 스타일링: 잘못된 선택일 경우 빨간색 테두리 */}
          <PickerButton
            className={isDisabled ? 'disabled' : ''}
            onClick={!isDisabled ? (e: any) => togglePicker(e) : undefined}
            style={{ outline: `${_invalid ? '2px solid red' : ''}` }}
            role='presentation'
          >
            {isIcon && <span className='material-symbols-rounded'>schedule</span>}
            {showSecond
              ? `${String(selectedTime.h).padStart(2, '0')}:${String(selectedTime.m).padStart(2, '0')}:${String(selectedTime.s).padStart(2, '0')}`
              : `${String(selectedTime.h).padStart(2, '0')}:${String(selectedTime.m).padStart(2, '0')}`}
          </PickerButton>
        </>
      )}

      {isOpen && (
        <PickerDropdown onMouseDown={handlePickerMouseDown} onMouseUp={handlePickerMouseUp}>
          <div className='flex-basic'>
            <div>
              <TimeHeader>{t('시')}</TimeHeader>
              <TimeColumn ref={hourWrapRef}>
                {hours.map((h) => (
                  <TimeOption
                    key={`h_${h}`}
                    disabled={limitTimeDisabled('hour', selectedTime, h) || false}
                    onClick={(e: any) => {
                      handleTimeSelect('hour', h, selectedTime.m, selectedTime.s, e, minUnitArr);
                      setIsUpdated?.(true);
                    }}
                    ref={h === selectedTime.h ? hourRef : null}
                    className={h === selectedTime.h ? 'active' : ''}
                    style={{ display: `${h >= 24 ? 'none' : 'block'}` }}
                  >
                    {String(h).padStart(2, '0')}
                  </TimeOption>
                ))}
              </TimeColumn>
            </div>
            <div>
              <TimeHeader>{t('분')}</TimeHeader>
              <TimeColumn style={{ height: minUnit === 30 ? '150px' : '' }} ref={minWrapRef}>
                {minUnit === 1
                  ? [...Array(64)].map((_, i) => (
                      <TimeOption
                        key={`m_${i}`}
                        disabled={limitTimeDisabled('min', selectedTime, i) || isDisabledFn(selectedTime.h, i, selectedTime.s)}
                        onClick={(e: any) => {
                          handleTimeSelect('min', selectedTime.h, i, selectedTime.s, e, minUnitArr);
                          setIsUpdated?.(true);
                        }}
                        ref={i === selectedTime.m ? minuteRef : null}
                        className={i === selectedTime.m ? 'active' : ''}
                        style={{ display: `${i >= 60 ? 'none' : 'block'}` }}
                      >
                        {String(i).padStart(2, '0')}
                      </TimeOption>
                    ))
                  : minUnitArr.map((min) => (
                      <TimeOption
                        key={min}
                        disabled={limitTimeDisabled('min', selectedTime, min) || isDisabledFn(selectedTime.h, min, selectedTime.s)}
                        onClick={(e: any) => {
                          handleTimeSelect('min', selectedTime.h, min, selectedTime.s, e, minUnitArr);
                          setIsUpdated?.(true);
                        }}
                        ref={min === selectedTime.m ? minuteRef : null}
                        className={min === selectedTime.m ? 'active' : ''}
                      >
                        {String(min).padStart(2, '0')}
                      </TimeOption>
                    ))}
              </TimeColumn>
            </div>
            {showSecond && (
              <div>
                <TimeHeader>{t('초')}</TimeHeader>
                <TimeColumn ref={secondWrapRef}>
                  {[...Array(64)].map((_, i) => (
                    <TimeOption
                      key={`s_${i}`}
                      disabled={isDisabledFn(selectedTime.h, selectedTime.m, i)}
                      onClick={(e: any) => handleTimeSelect('sec', selectedTime.h, selectedTime.m, i, e)}
                      ref={i === selectedTime.s ? secondRef : null}
                      className={i === selectedTime.s ? 'active' : ''}
                      style={{ display: `${i >= 60 ? 'none' : 'block'}` }}
                    >
                      {String(i).padStart(2, '0')}
                    </TimeOption>
                  ))}
                </TimeColumn>
              </div>
            )}
          </div>
          {/* <Btns>
            <BtnGhost onClick={() => setIsOpen(false)}>취소</BtnGhost>
            <BtnGhost onClick={() => setIsOpen(false)}>적용</BtnGhost>
          </Btns> */}
        </PickerDropdown>
      )}
    </TimePickerContainer>
  );
};

export default CustomTimePicker;
